import type { ThemeKeys, ThemeType } from '@vedalib/editor/lib/brand'
import { IconsAlignEnum, KEYS } from '@vedalib/editor/lib/brand'
import { setThemeVariable } from '@vedalib/editor/lib/brand/utils'
import { ObjectFitEnum } from '@vedalib/editor/lib/files'
import cn from 'classnames'

import type { IArrayOption } from 'components/formKit/fields/FieldNumberArray/FieldNumberArray'
import type { IColorOption } from 'components/uiKit/ColorPicker'
import { ColorOption } from 'components/uiKit/ColorPicker'
import { Tooltip } from 'components/uiKit/Dropdown'
import Icon from 'components/uiKit/Icon'
import { KitSize } from 'components/uiKit/KitTypes'
import { t } from 'services/Translation'
import { getEnumOption } from 'utils/enum'

import * as s from './constants.module.scss'
import { BRAND_THEME_LABELS } from './labels'

export const HORIZONTAL_ALIGN_OPTIONS = [
  {
    value: 'flex-start' as const,
    label: <Icon name='alignmentAlignLeft' size={KitSize.S} />,
    tooltip: t('editorV2.flex.align.start'),
  },
  {
    value: 'center' as const,
    label: <Icon name='alignmentAlignMid' size={KitSize.S} />,
    tooltip: t('editorV2.flex.align.center'),
  },
  {
    value: 'flex-end' as const,
    label: <Icon name='alignmentAlignRight' size={KitSize.S} />,
    tooltip: t('editorV2.flex.align.end'),
  },
]

export const VERTICAL_ALIGN_OPTIONS = [
  {
    value: 'flex-start' as const,
    label: <Icon name='alignmentAlignTop' size={KitSize.S} />,
    tooltip: t('editorV2.flex.align.top'),
  },
  {
    value: 'center' as const,
    label: <Icon name='alignmentAlignCenter' size={KitSize.S} />,
    tooltip: t('editorV2.flex.align.center'),
  },
  {
    value: 'flex-end' as const,
    label: <Icon name='alignmentAlignBottom' size={KitSize.S} />,
    tooltip: t('editorV2.flex.align.bottom'),
  },
]

export const OBJECT_FIT_OPTIONS = [
  getEnumOption('ObjectFitEnum', ObjectFitEnum.cover),
  getEnumOption('ObjectFitEnum', ObjectFitEnum.contain),
  getEnumOption('ObjectFitEnum', ObjectFitEnum.fill),
]

export const FONT_SIZE_OPTIONS = [
  { value: 8, label: '8' },
  { value: 9, label: '9' },
  { value: 10, label: '10' },
  { value: 11, label: '11' },
  { value: 12, label: '12' },
  { value: 13, label: '13' },
  { value: 14, label: '14' },
  { value: 15, label: '15' },
  { value: 16, label: '16' },
  { value: 18, label: '18' },
  { value: 20, label: '20' },
  { value: 22, label: '22' },
  { value: 24, label: '24' },
  { value: 28, label: '28' },
  { value: 32, label: '32' },
  { value: 36, label: '36' },
  { value: 42, label: '42' },
  { value: 56, label: '56' },
  { value: 72, label: '72' },
]

export const BORDER_OPTIONS = [
  { value: 'none' as const, label: t('common.no') },
  { value: 'solid' as const, label: t('common.yes') },
]

export const DISPLAY_OPTIONS = [
  { value: 'none' as const, label: t('common.no') },
  { value: 'flex' as const, label: t('common.yes') },
]

export const DISPLAY_ICON_OPTIONS = [
  { value: false, label: t('common.no') },
  { value: true, label: t('common.yes') },
]

export const ICON_ALIGN_OPTIONS = [
  { value: IconsAlignEnum.hide, label: <Icon name='otherBlock' size={KitSize.S} /> },
  { value: IconsAlignEnum.leftUp, label: <Icon name='builderLeftUp' size={KitSize.S} /> },
  { value: IconsAlignEnum.rightUp, label: <Icon name='builderRightUp' size={KitSize.S} /> },
  { value: IconsAlignEnum.leftDown, label: <Icon name='builderLeftDown' size={KitSize.S} /> },
  { value: IconsAlignEnum.rightDown, label: <Icon name='builderRightDown' size={KitSize.S} /> },
]

export const LINE_HEIGHT_OPTIONS = [
  { label: '1', value: 1 },
  { label: '1.5', value: 1.5 },
  { label: '2', value: 2 },
  { label: 'auto', value: 'normal' },
]

export const OFFSET_ARRAY: IArrayOption[] = [
  { icon: <Icon name='builderInputUp' size={KitSize.XS} /> },
  { icon: <Icon name='builderInputForward' size={KitSize.XS} /> },
  { icon: <Icon name='builderInputDown' size={KitSize.XS} /> },
  { icon: <Icon name='builderInputBack' size={KitSize.XS} /> },
]

export const FONT_ITALIC_PARAMS = {
  style: { padding: 0 },
  hideCheckbox: true,
  option: {
    label: ({ disabled }: { disabled?: boolean }) => (
      <div className={cn(s.italic, { [s.disabled]: disabled })}>
        <Tooltip disabled={disabled} overlay={t('uiKit.tooltip.italic')} placement='bottom-end'>
          <Icon name='builderItalic' size={KitSize.S} />
        </Tooltip>
      </div>
    ),
    valueTrue: 'italic' as const,
    valueFalse: 'normal' as const,
  },
}

export const FLEX_DIRECTION_OPTIONS = [
  {
    label: (
      <Tooltip overlay={t('elements.layoutList.tooltip.row')}>
        <Icon name='builderArrowRight' size={KitSize.S} />
      </Tooltip>
    ),
    value: 'row' as const,
  },
  {
    label: (
      <Tooltip overlay={t('elements.layoutList.tooltip.column')}>
        <Icon name='builderArrowDown' size={KitSize.S} />
      </Tooltip>
    ),
    value: 'column' as const,
  },
]

export const COLORS_THEME = [
  KEYS.primaryColor,
  KEYS.secondaryColor,
  KEYS.hoverColor,
  KEYS.focusedColor,
  KEYS.inactiveColor,
  KEYS.neutralColor,
  KEYS.borderColor,
]

export const TEXT_COLORS = [
  KEYS.primaryTextColor,
  KEYS.secondaryTextColor,
  KEYS.lightTextColor,
  KEYS.accentTextColor,
]

export const BACKGROUND_COLORS = [
  KEYS.primaryBackgroundColor,
  KEYS.darkBackgroundColor,
  KEYS.lightBackgroundColor,
  KEYS.accentBackgroundColor,
]

export const FEEDBACK_COLORS = [
  KEYS.infoColor,
  KEYS.warningColor,
  KEYS.successColor,
  KEYS.errorColor,
]

export const genColorOptions = (theme: ThemeType): IColorOption[] => [
  {
    value: 'brand',
    label: t('input.option.brandColors'),
    options: COLORS_THEME.map((key) => ({
      value: setThemeVariable(key),
      label: genColorLabel(key, theme[key] as string),
      rootLabel: `${t('input.option.brandColors')}/${BRAND_THEME_LABELS[key]}`,
      color: String(theme[key]),
    })),
  },
  {
    value: 'text',
    label: t('input.option.textColors'),
    options: TEXT_COLORS.map((key) => ({
      value: setThemeVariable(key),
      label: genColorLabel(key, theme[key] as string),
      rootLabel: `${t('input.option.textColors')}/${BRAND_THEME_LABELS[key]}`,
      color: String(theme[key]),
    })),
  },
  {
    value: 'background',
    label: t('input.option.backgroundColors'),
    options: BACKGROUND_COLORS.map((key) => ({
      value: setThemeVariable(key),
      label: genColorLabel(key, theme[key] as string),
      rootLabel: `${t('input.option.backgroundColors')}/${BRAND_THEME_LABELS[key]}`,
      color: String(theme[key]),
    })),
  },
  {
    value: 'feedback',
    label: t('input.option.additionalColors'),
    options: FEEDBACK_COLORS.map((key) => ({
      value: setThemeVariable(key),
      label: genColorLabel(key, theme[key] as string),
      rootLabel: `${t('input.option.additionalColors')}/${BRAND_THEME_LABELS[key]}`,
      color: String(theme[key]),
    })),
  },
]

export const genColorLabel = (name: ThemeKeys, color: string) => (
  <ColorOption color={color} name={BRAND_THEME_LABELS[name]} />
)
